import { createErrorHandler } from '@rjsf/utils';

const isRequiredFieldMissing = (fieldValue) =>
  fieldValue === undefined || fieldValue === null || fieldValue === '';

const getFieldTitle = (fieldName, formSchema) =>
  formSchema.properties[fieldName]?.title ||
  `${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;

/**
 * @example
 * const customValidate = (formData, errors) => {
 *   addRequiredFieldErrors(formData, errors, schema);
 *   addRequiredObjectErrors(formData.options, errors.options, optionsSchema);
 *   return errors;
 * };
 *
 * const transformErrors = (errors) =>
 *   suppressNestedRequiredFieldErrors(
 *     suppressRequiredFieldErrors(errors, schema),
 *     optionsSchema
 *   );
 */

/**
 * Adds title-based messages for missing root-level fields from a JSON schema.
 * Intended for RJSF customValidate callbacks; zero and false are valid field values.
 */
const addRequiredFieldErrors = (formData, validationErrors, formSchema) => {
  for (const fieldName of formSchema.required) {
    if (isRequiredFieldMissing(formData[fieldName])) {
      validationErrors[fieldName].addError(`${getFieldTitle(fieldName, formSchema)} is required.`);
    }
  }

  return validationErrors;
};

/** Adds required-field messages to the corresponding child nodes of a nested RJSF error object. */
const addRequiredObjectErrors = (formData, validationErrors, formSchema) => {
  for (const fieldName of formSchema.required) {
    if (isRequiredFieldMissing(formData[fieldName])) {
      validationErrors[fieldName] ??= createErrorHandler();
      validationErrors[fieldName].addError(`${getFieldTitle(fieldName, formSchema)} is required.`);
    }
  }

  return validationErrors;
};

/** Removes root-level schema errors replaced by custom required messages. */
const suppressRequiredFieldErrors = (validationErrors, formSchema) =>
  validationErrors.filter(
    (validationError) =>
      validationError.name !== 'required' || !formSchema.required.includes(validationError.property)
  );

/** Removes nested schema errors replaced by field-level custom required messages. */
const suppressNestedRequiredFieldErrors = (validationErrors, formSchema) =>
  validationErrors.filter(
    (validationError) =>
      validationError.name !== 'required' ||
      !formSchema.required.includes(validationError.property?.split('.').at(-1))
  );

export {
  addRequiredFieldErrors,
  addRequiredObjectErrors,
  suppressNestedRequiredFieldErrors,
  suppressRequiredFieldErrors,
};
